"use client";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import {
  analyticsAllowed,
  collectLandingEvent,
  flushLandingEvents,
  privacySignal,
  setAnalyticsChoice,
} from "../api/analytics.api";
const clickMap: Record<
  string,
  {
    name: "cta_click" | "story_step" | "blox_open" | "mobile_view";
    element: string;
  }
> = {
  "header-start": { name: "cta_click", element: "header-start" },
  "hero-primary": { name: "cta_click", element: "hero-primary" },
  "hero-try": { name: "cta_click", element: "hero-try" },
  "footer-start": { name: "cta_click", element: "footer-start" },
  "mobile-section-start": {
    name: "cta_click",
    element: "mobile-section-start",
  },
  "phone-start": { name: "cta_click", element: "phone-start" },
  "story-signup": { name: "cta_click", element: "story-signup" },
  "story-meaning": { name: "story_step", element: "story-meaning" },
  "story-memory": { name: "story_step", element: "story-memory" },
  "mobile-list": { name: "mobile_view", element: "mobile-list" },
  "mobile-timeline": { name: "mobile_view", element: "mobile-timeline" },
  ...Object.fromEntries(
    ["family", "journalling", "fitness", "memories"].map((name) => [
      "sample-" + name,
      { name: "blox_open" as const, element: "sample-" + name },
    ]),
  ),
};
export function useLandingAnalytics() {
  const path = usePathname();
  const [choiceOpen, setChoiceOpen] = useState(false),
    [revision, setRevision] = useState(0);
  const page =
    path === "/"
      ? "home"
      : path === "/signup"
        ? "signup"
        : path === "/login"
          ? "login"
          : null;
  useEffect(() => {
    function open() {
      setChoiceOpen(true);
    }
    window.addEventListener("lb-privacy-choice", open);
    let decided = false;
    try {
      decided = !!localStorage.getItem("lb-analytics-choice");
    } catch {
      /* disabled */
    }
    if (!decided && !privacySignal()) {
      const timer = setTimeout(open, 500);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("lb-privacy-choice", open);
      };
    }
    return () => window.removeEventListener("lb-privacy-choice", open);
  }, []);
  useEffect(() => {
    if (!page || !analyticsAllowed()) return;
    collectLandingEvent("page_view", page);
    if (page === "signup") collectLandingEvent("signup_view", page);
    const depths = new Set<number>();
    function click(event: MouseEvent) {
      const target =
        event.target instanceof Element
          ? event.target.closest<HTMLElement>("[data-analytics]")
          : null;
      const item = target
        ? clickMap[target.dataset.analytics ?? ""]
        : undefined;
      if (item) collectLandingEvent(item.name, page!, item.element);
    }
    function scroll() {
      if (page !== "home") return;
      const ratio =
        ((scrollY + innerHeight) /
          Math.max(document.documentElement.scrollHeight, 1)) *
        100;
      for (const depth of [25, 50, 75, 100])
        if (ratio >= depth && !depths.has(depth)) {
          depths.add(depth);
          collectLandingEvent("scroll_depth", "home", "depth-" + depth);
        }
    }
    function flush() {
      void flushLandingEvents();
    }
    const timer = setInterval(flush, 5000);
    // Capture the semantic control before React changes its next-step label.
    document.addEventListener("click", click, true);
    window.addEventListener("scroll", scroll, { passive: true });
    window.addEventListener("pagehide", flush);
    scroll();
    flush();
    return () => {
      clearInterval(timer);
      document.removeEventListener("click", click, true);
      window.removeEventListener("scroll", scroll);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [page, revision]);
  return {
    visible: !!page && choiceOpen,
    choose: (allowed: boolean) => {
      setAnalyticsChoice(allowed);
      setChoiceOpen(false);
      setRevision((n) => n + 1);
    },
    blocked: typeof navigator !== "undefined" && privacySignal(),
  };
}
